import { useCallback, useEffect, useRef, useState } from "react";
import { dashboard, type DailyPulseOverview } from "services/api";
import { AssignedCourseSummary, CourseProgressData } from "types";
import { preloadCourseImages } from "utils/courseImagePreload";

export interface StreakSummary {
  streak_days: number;
  last_active_date: string | null;
  timezone: string;
}

export interface ActiveCourseSummary {
  id: number;
  name: string;
}

export type DailyCheckinSummary = DailyPulseOverview;

export interface LegacyDailyScoresDay {
  date: string;
  label: string;
  weekday_index: number;
  is_today: boolean;
  is_current_week: boolean;
  is_completed: boolean;
  flow_run_id?: number | null;
  completed_at?: string | null;
}

export interface LegacyDailyScoresSummary {
  flow_id: number;
  flow_name: string;
  weekly_status: boolean[];
  days: LegacyDailyScoresDay[];
  has_preferences: boolean;
  timezone: string;
  window_start: string;
  window_end: string;
  weekly_scores: Record<string, number | null>;
  read_only: true;
}

export interface DashboardSummary {
  course_progress: CourseProgressData | null;
  streak: StreakSummary | null;
  active_course: ActiveCourseSummary | null;
  daily_checkin?: DailyCheckinSummary | null;
  legacy_daily_scores?: LegacyDailyScoresSummary | null;
  assigned_courses?: AssignedCourseSummary[];
  course_error?: unknown;
}

export const useDashboardSummary = () => {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dashboard.summary();
      const typedResult = result as DashboardSummary;
      preloadCourseImages(typedResult.course_progress);
      if (isMountedRef.current) {
        setData(typedResult);
        setError(null);
      }
      return typedResult;
    } catch (err) {
      console.error("Failed to load dashboard summary:", err);
      if (isMountedRef.current) {
        setError("Failed to load dashboard data");
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchSummary().catch(() => null);
  }, [fetchSummary]);

  return {
    data,
    loading,
    error,
    refetch: fetchSummary,
  };
};

export default useDashboardSummary;
